import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { WorkflowOrganisationAccessLevel } from "../../../../../constants";
import { parseProjectPhase } from "../../../../../constants/project-phase";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { Origin, ProjectDatesData, UpcomingMilestonePropsWithKey, WorkflowOrganisation } from "../../../../../models";
import { IStateOrigin } from "../../../../../navigators/navigateStateObjects";
import {
  getAggregateWorkflowTasks,
  GetAggregateWorkflowTasksResponse,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import {
  getDashboardRoute,
  getOperationsDealWorkflowsRoute,
  getProjectTasksRoute,
  getProjectTrackerRoute,
  getWorkflowTemplatesRoute,
} from "../../../../../utils/routes";
import { dataGridMapFilterCriteria, ProfileMenuItemType } from "../../../../../widget";
import { DealStageItem } from "../../../../asset-manager/deal/components";
import { TasksBoardContext } from "./TasksBoardContext";

interface useTasksBoardReturnData {
  projectUuid: string | undefined;
  objectUuid: string | undefined;
  objectName: string | undefined;
  objectRowVersion: number | undefined;
  workflowUuid: string | undefined;
  tasksBoard: GetTaskBoardResponse | undefined;
  workflowOrganisationAccessLevel: WorkflowOrganisationAccessLevel | undefined;
  workflowOrganisations: WorkflowOrganisation[] | undefined;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  isDealWorkflow: boolean;
  dealStages: DealStageItem[];
  currentDealStage: string | undefined;
  handleDealStageChange: (stage: string) => void;

  showComplete: boolean;
  handleShowCompleteChange: () => void;

  showAddWorkflowStepPanel: boolean;
  setShowAddWorkflowStepPanel: Dispatch<SetStateAction<boolean>>;

  showRemoveWorkflowModal: boolean;
  setShowRemoveWorkflowModal: Dispatch<SetStateAction<boolean>>;

  showEditDatesPanel: boolean;
  closeEditDatesPanel: () => void;
  handleDealDatesSaved: () => void;

  projectDates: ProjectDatesData | undefined;
  setProjectDates: Dispatch<SetStateAction<ProjectDatesData | undefined>>;

  showUpcomingMilestonesPanel: boolean;
  setShowUpcomingMilestonesPanel: Dispatch<SetStateAction<boolean>>;

  showWorkflowTaskPanel: boolean;
  setShowWorkflowTaskPanel: Dispatch<SetStateAction<boolean>>;

  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;

  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: Dispatch<SetStateAction<boolean>>;

  refreshTasksBoard: boolean;
  shouldRefreshTasksBoard: () => void;

  handleCloseWorkflowTaskPanel: () => void;
  hasAccessLevelAndCanEditTemplate: (accessLevel: WorkflowOrganisationAccessLevel) => boolean;
  handleBackClick: () => void;
  goBackText: string;
  upcomingMilestones: UpcomingMilestonePropsWithKey[];

  toggleProgress: boolean;
  taskProgress: GetAggregateWorkflowTasksResponse | undefined;
  onToggle: (value: boolean) => void;

  showWorkflowPermissionsPanel: boolean;
  setShowWorkflowPermissionsPanel: Dispatch<SetStateAction<boolean>>;
}

export const useTasksBoard = (): useTasksBoardReturnData => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const [searchParams] = useSearchParams();
  const origin = searchParams.get("origin");

  const { currentUserType } = useAuth();
  const {
    projectUuid,
    workflowUuid,
    tasksBoard,
    workflowOrganisationAccessLevel,
    workflowOrganisations,
    projectDates,
    setProjectDates,
    showEditDatesPanel,
    setShowEditDatesPanel,
    selectedWorkflowStage,
    isDealWorkflow,
    dealStages,
    currentDealStage,
    handleDealStageChange,
    showComplete,
    handleShowCompleteChange,
    shouldRefreshTasksBoard,
    refreshTasksBoard,
    hasAccessLevelAndCanEditTemplate,
  } = useContext(TasksBoardContext);

  let goBackText = stateOrigin?.goBackText;

  if (!goBackText) {
    switch (origin) {
      case Origin.ProjectTasksPage:
        goBackText = "Back to project";
        break;
      case Origin.DashboardPage:
        goBackText = "Back to dashboard";
        break;
      case Origin.ProjectTrackerPage:
        goBackText = "Back to projects";
        break;
      default:
        goBackText = "Back to workflows";
        break;
    }
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin?.from);
      return;
    }
    switch (origin) {
      case Origin.ProjectTasksPage:
        navigate(getProjectTasksRoute(tasksBoard?.results[0].projectUuid || "", currentUserType));
        break;
      case Origin.DashboardPage:
        navigate(getDashboardRoute(currentUserType));
        break;
      case Origin.ProjectTrackerPage:
        navigate(getProjectTrackerRoute(currentUserType));
        break;
      default:
        navigate(
          isDealWorkflow ? getOperationsDealWorkflowsRoute(currentUserType) : getWorkflowTemplatesRoute(currentUserType)
        );
        break;
    }
  };

  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const [showAddWorkflowStepPanel, setShowAddWorkflowStepPanel] = useState(false);
  const [showRemoveWorkflowModal, setShowRemoveWorkflowModal] = useState(false);
  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [showUpcomingMilestonesPanel, setShowUpcomingMilestonesPanel] = useState(false);
  const [toggleProgress, setToggleProgress] = useState(false);
  const [taskProgress, setTaskProgress] = useState<GetAggregateWorkflowTasksResponse | undefined>();
  const [showWorkflowPermissionsPanel, setShowWorkflowPermissionsPanel] = useState(false);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  const editDatesAction = (): void => {
    setShowEditDatesPanel(true);
  };

  const quickActionsMenuOptionsList: ProfileMenuItemType[] = hasAccessLevelAndCanEditTemplate(
    WorkflowOrganisationAccessLevel.Owner
  )
    ? [
        ...(!isDealWorkflow
          ? [
              {
                id: 1,
                value: "Edit access",
                action: () => setShowWorkflowPermissionsPanel(true),
              },
            ]
          : []),
        {
          id: 2,
          value: isDealWorkflow ? "Edit stage dates" : "Edit phase dates",
          action: editDatesAction,
        },
        {
          id: 3,
          value: "Remove workflow",
          action: () => setShowRemoveWorkflowModal(true),
        },
      ]
    : [
        {
          id: 1,
          value: isDealWorkflow ? "Edit stage dates" : "Edit phase dates",
          action: editDatesAction,
        },
      ];

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const searchUpcomingMilestoneTasks = async (): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.workflowUuid = {
      operator: "in",
      value: [workflowUuid || ""],
    };

    filterCriteria.deadlineDate = {
      operator: "neq",
      value: null,
    };

    filterCriteria.isMilestone = {
      operator: "eq",
      value: true,
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    const result = await searchWorkflowTasks({
      paging: {
        limit: 4,
        afterCursor: null,
        beforeCursor: null,
      },
      filter: {
        results: filterCriteria,
      },
      sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
    });
    if (result.status !== Status.Success || result.data === undefined) {
      return;
    }

    setUpcomingMilestones(
      result.data.results.map((task) => ({
        key: task.uuid,
        name: task.name,
        deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
        status: task.status,
        assigneeName: task.assignee?.name ?? undefined,
        onClick: () => {
          setSelectedPanelTask(task);
          setShowWorkflowTaskPanel(true);
        },
      }))
    );
  };
  useEffect(() => {
    searchUpcomingMilestoneTasks();
  }, [workflowUuid, refreshTasksBoard]);

  const searchTasksProgressKPI = async (toggle = toggleProgress): Promise<void> => {
    let phase = null;
    if (!toggle && selectedWorkflowStage) {
      if (isDealWorkflow) {
        phase = selectedWorkflowStage;
      } else {
        phase = parseProjectPhase(selectedWorkflowStage) ?? selectedWorkflowStage;
      }
    }
    const result = await getAggregateWorkflowTasks({
      phase,
      dealUuid: null,
      projectUuid: null,
      activityUuid: null,
      workflowUuid: workflowUuid || null,
    });

    if (result.status !== Status.Success || result.data === undefined) {
      return;
    }

    setTaskProgress(result.data);
  };

  useEffect(() => {
    searchTasksProgressKPI();
  }, [workflowUuid, refreshTasksBoard, selectedWorkflowStage]);

  const handleToggle = async (newValue: boolean): Promise<void> => {
    setToggleProgress(newValue);
    await searchTasksProgressKPI(newValue);
  };

  const closeEditDatesPanel = (): void => {
    setShowEditDatesPanel(false);
  };

  const handleDealDatesSaved = (): void => {
    setShowEditDatesPanel(false);
    shouldRefreshTasksBoard();
  };

  return {
    projectUuid,
    objectUuid: tasksBoard?.results[0].dealUuid ?? tasksBoard?.results[0].projectUuid ?? undefined,
    objectName: tasksBoard?.results[0].dealName ?? tasksBoard?.results[0].projectDisplayName ?? undefined,
    objectRowVersion: tasksBoard?.results[0].dealRowVersion ?? tasksBoard?.results[0].projectRowVersion ?? undefined,
    workflowUuid,
    tasksBoard,
    workflowOrganisationAccessLevel,
    workflowOrganisations,
    workflow: tasksBoard?.results[0],
    isDealWorkflow,
    dealStages,
    currentDealStage,
    handleDealStageChange,

    showComplete,
    handleShowCompleteChange,

    showAddWorkflowStepPanel,
    setShowAddWorkflowStepPanel,

    showRemoveWorkflowModal,
    setShowRemoveWorkflowModal,

    showEditDatesPanel,
    closeEditDatesPanel,
    handleDealDatesSaved,

    showUpcomingMilestonesPanel,
    setShowUpcomingMilestonesPanel,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,

    selectedPanelTask,
    setSelectedPanelTask,

    projectDates,
    setProjectDates,

    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,

    refreshTasksBoard,
    shouldRefreshTasksBoard,

    handleCloseWorkflowTaskPanel,
    hasAccessLevelAndCanEditTemplate,
    handleBackClick,
    goBackText,
    upcomingMilestones,
    toggleProgress,
    taskProgress,
    onToggle: handleToggle,

    showWorkflowPermissionsPanel,
    setShowWorkflowPermissionsPanel,
  };
};
